import React, { FC, useCallback, useState, useMemo } from "react";

import { ContactsTable } from "./ContactsTable";
import { useGetTeamContacts } from "./queries/useContactsTable";
import { IContact } from "./types/Contact";
import { PageSetup } from "../../components";
import { AddTagsToContact } from "./components/AddTagsToContact";
import { useGetTeamTags } from "./queries/useTags";
import { FEATURES } from "constants/features";
import { CONTACTS_TEXTS } from "constants/Texts";
import { useContactsFilter } from "./hooks/useContactsFilter";
import { useContactsSort } from "./hooks/useContactsSort";
import { useContactsPagination } from "./hooks/useContactsPagination";
import { getSortOptionFromModel } from "./utils/sortUtils";
import TopHeader from "./components/TopHeader";
import { useDisclosure } from "@chakra-ui/react";

interface IContactsProps {
  teamId: number;
}

export const Contacts: FC<IContactsProps> = ({ teamId }) => {
  const {
    isOpen: isAddTagsToContactOpen,
    onOpen: onAddTagsToContactOpen,
    onClose: onAddTagsToContactClose,
  } = useDisclosure();

  const [currentContact, setCurrentContact] = useState<IContact | undefined>();

  const { filterModel, serverFilterModel, handleFilterModelChange } =
    useContactsFilter({
      teamId,
    });
  const { sortModel, handleSortModelChange } = useContactsSort();
  const { paginationModel, paginationState, handlePaginationChange } =
    useContactsPagination();

  const {
    data: teamContactsData,
    refetch: refetchContactsData,
    isLoading: isLoadingTeamContactsData,
  } = useGetTeamContacts({
    teamId,
    ...serverFilterModel,
    sort: getSortOptionFromModel(sortModel),
    offset: paginationState.offset,
    limit: paginationState.limit,
  });

  const { data: teamTagsData, refetch: refetchTeamTagsData } = useGetTeamTags({
    teamId,
  });

  const handleManageContactTags = useCallback(
    (contact: IContact) => {
      setCurrentContact(contact);
      onAddTagsToContactOpen();
    },
    [onAddTagsToContactOpen],
  );

  const handleCloseAddTagsToContact = useCallback(() => {
    setCurrentContact(undefined);
    onAddTagsToContactClose();
  }, [onAddTagsToContactClose]);

  const teamContacts = useMemo(
    () => teamContactsData?.results || [],
    [teamContactsData?.results],
  );
  const teamTags = useMemo(
    () => teamTagsData?.results || [],
    [teamTagsData?.results],
  );

  const rowCount = teamContactsData?.count || 0;

  const filters = useMemo(
    () => ({
      teamId,
      ...serverFilterModel,
      sort: getSortOptionFromModel(sortModel),
    }),
    [teamId, serverFilterModel, sortModel],
  );

  return (
    <PageSetup
      title={CONTACTS_TEXTS.CONTACTS_PAGE.TITLE}
      subTitle={CONTACTS_TEXTS.CONTACTS_PAGE.SUB_TITLE}
      visibleInMobile
      rightBodyElement={
        <TopHeader
          refetchContactsData={refetchContactsData}
          canExport={Boolean(teamContacts.length)}
          filters={filters}
        />
      }
      feature={FEATURES.CONTACTS}
    >
      <>
        <ContactsTable
          refetchContactsData={refetchContactsData}
          refetchTeamTagsData={refetchTeamTagsData}
          contacts={teamContacts}
          contactsCount={rowCount}
          isLoadingContacts={isLoadingTeamContactsData}
          onManageContactTags={handleManageContactTags}
          onPaginationChange={handlePaginationChange}
          paginationModel={paginationModel}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          teamTags={teamTags}
          teamId={teamId}
        />

        {currentContact && (
          <AddTagsToContact
            contact={currentContact}
            teamId={teamId}
            refetchContactsData={refetchContactsData}
            isOpen={isAddTagsToContactOpen}
            onClose={handleCloseAddTagsToContact}
          />
        )}
      </>
    </PageSetup>
  );
};
